/**
 * Data hooks of the editor's plan panels: the design's kept savings inputs (saved as they change), live savings for
 * the unsaved panels, and the design's setbacks with a live preview of other rules. The editor holds them so the
 * side panels and the presentation share one state.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { editorObstacles, editorPanels, type ObstacleItem, type PanelItem } from "./model.ts";

const SAVE_DEBOUNCE_MS = 900;
const LIVE_DEBOUNCE_MS = 700;

/** A request without its null fields (the API reads a missing field as "use the settings"). */
export function compact<T>(value: T): T {
  if (Array.isArray(value)) return value.map(compact) as T;
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) if (v != null) out[k] = compact(v);
    return out as T;
  }
  return value;
}

export type SaveState = "idle" | "saving" | "saved" | "error";

/** The design's savings inputs: loaded once, kept locally, saved (debounced) when the user may edit the design. */
export function useSavingsInputs(designId: string, canEdit: boolean) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["savings-inputs", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/savings-inputs", { params: { path: { design_id: designId } } })),
  });
  const [inputs, setLocal] = useState<S["SavingsRequest"] | null>(null);
  const [state, setState] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (query.data && inputs == null) setLocal(compact(query.data.inputs));
  }, [query.data, inputs]);

  const setInputs = useCallback(
    (next: S["SavingsRequest"]) => {
      setLocal(next);
      if (!canEdit) return;
      if (timer.current) window.clearTimeout(timer.current);
      setState("saving");
      timer.current = window.setTimeout(() => {
        unwrap(api.PUT("/v1/designs/{design_id}/savings-inputs", { params: { path: { design_id: designId } }, body: compact(next) }))
          .then((r) => {
            qc.setQueryData(["savings-inputs", designId], r);
            setState("saved");
            setError(null);
          })
          .catch((e) => {
            setState("error");
            setError(errorMessage(e));
          });
      }, SAVE_DEBOUNCE_MS);
    },
    [canEdit, designId, qc],
  );
  useEffect(() => () => void (timer.current && window.clearTimeout(timer.current)), []);
  return { inputs: inputs ?? {}, loaded: inputs != null, setInputs, state, error, updatedAt: query.data?.updated_at ?? null };
}

/** Savings for the editor's current panels (debounced; the last answer wins). */
export function useLiveSavings(designId: string, version: number, panels: PanelItem[], inputs: S["SavingsRequest"], enabled: boolean) {
  const [result, setResult] = useState<S["SavingsResult"] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const body = useMemo(() => JSON.stringify({ panels: editorPanels(panels), savings: compact(inputs) }), [panels, inputs]);
  useEffect(() => {
    if (!enabled) return;
    const id = ++seq.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      const parsed = JSON.parse(body) as { panels: S["EditorPanel"][]; savings: S["SavingsRequest"] };
      unwrap(api.POST("/v1/designs/{design_id}/editor/savings", { params: { path: { design_id: designId } }, body: { version_number: version, ...parsed } }))
        .then((r) => {
          if (id !== seq.current) return;
          setResult(r);
          setError(null);
        })
        .catch((e) => id === seq.current && setError(errorMessage(e)))
        .finally(() => id === seq.current && setLoading(false));
    }, LIVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [body, designId, version, enabled]);
  return { result, loading, error };
}

/** The design's setbacks (company, override, presets) and the zones of the rules being edited, previewed live. */
export function useDesignSetbacks(designId: string, version: number, obstacles: ObstacleItem[], obstaclesDirty: boolean) {
  const query = useQuery({
    queryKey: ["design-setbacks", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/setbacks", { params: { path: { design_id: designId } } })),
  });
  const [draft, setDraft] = useState<S["SetbacksSettings"] | null>(null);
  const [preview, setPreview] = useState<S["SetbacksOutput"] | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const seq = useRef(0);
  // what to preview: only the rules being edited. Rules that changed since the version (the company's, or the
  // design's override kept earlier) are information with an explicit Apply, never drawn or folded into the editor's
  // state on open: opening a saved design is never "unsaved" (QA round 3, LT-41/LT-51)
  const rules = draft;
  const key = useMemo(() => (rules ? JSON.stringify({ rules, o: obstaclesDirty ? editorObstacles(obstacles) : null }) : null), [rules, obstacles, obstaclesDirty]);
  useEffect(() => {
    if (!key) {
      setPreview(null);
      return;
    }
    const id = ++seq.current;
    setPreviewing(true);
    const timer = window.setTimeout(() => {
      const parsed = JSON.parse(key) as { rules: S["SetbacksSettings"]; o: S["EditorObstruction"][] | null };
      unwrap(
        api.POST("/v1/designs/{design_id}/setbacks/preview", {
          params: { path: { design_id: designId } },
          body: { version_number: version, rules: parsed.rules, ...(parsed.o ? { obstructions: parsed.o } : {}) },
        }),
      )
        .then((r) => id === seq.current && setPreview(r))
        .catch(() => id === seq.current && setPreview(null))
        .finally(() => id === seq.current && setPreviewing(false));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [key, designId, version]);
  return { state: query.data ?? null, loading: query.isLoading, refetch: query.refetch, draft, setDraft, preview, previewing, rulesChanged: query.data?.applied === false };
}
